<template>
  <div v-if="disasterNo && isAuth">
    <!-- 共通ヘッダー（タイトル/サブタイトル） -->
    <div class="content-header">
      <h1 class="page-title">
        <i class="far fa-hospital"></i>
        避難対象の医療機関等確認
      </h1>
    </div>
    <error-message :error-object="errorObject" />
    <success-message :success-object="successObject" />
    <section>
      <!-- 検索部 -->
      <details :open="isOpen" v-if="isAuth" ref="evacuation_target_vul_search">
        <summary @click.prevent="toggleOpen">検索条件</summary>
        <div class="content-card compact">
          <h2 class="card-title">避難対象の医療機関等の検索</h2>
          <div
            class="sample-content"
            style="display:flex; gap:16px; align-items:flex-start; position:relative;"
          >
            <div
              style="display:flex; gap:16px; flex-wrap:wrap; align-items:flex-start;"
          >
            <div style="flex-shrink:0;">
              <!-- 風向き選択 -->
              <div style="font-weight:500; color:#666; font-size:14px;">
                ①避難不可の方角を指定してください。<br />（複数可）
              </div>
              <SelectDirection
                ref="selectDirection"
                style="margin-left:30px;"
              />
            </div>

            <div
              style="display:flex; gap:16px; flex-wrap:wrap; align-items:flex-start;"
            >
              <!-- モニタリングポスト選択 -->
              <SelectMonitoringPost ref="selectMonitoringPost" />
              </div>

              <div style="flex-shrink:0;">
              <!-- ③避難対象の地区検索 -->
              <div
                style="display:flex; flex-direction:column; gap:4px; min-width:300px; margin-left:30px;"
              >
                <div
                  style="margin-bottom:3px; font-weight:500; color:#666; font-size:14px;"
                >
                  ③避難対象の地区を検索できます。
                </div>

                <div style="display:flex; flex-direction:column; gap:4px;">
                  <div
                    style="display:flex; align-items:center; gap:8px; margin:1px 0;"
                  >
                    <div class="vtf-field" style="width:120px;">
                      <input
                        v-model="searchFilters.municipName"
                        type="text"
                        placeholder=" "
                        maxlength="30"
                      />
                      <label>市町名</label>
                    </div>
                    <div class="hint">部分一致</div>
                  </div>
                  <div
                    style="display:flex; align-items:center; gap:8px; margin:1px 0;"
                  >
                    <div class="vtf-field" style="width:120px;">
                      <input
                        v-model="searchFilters.areaName"
                        type="text"
                        placeholder=" "
                        maxlength="50"
                      />
                      <label>地区名</label>
                    </div>
                    <div class="hint">部分一致</div>
                  </div>
                  <div style="margin:1px 0;">
                    <div
                      style="margin-bottom:4px; font-weight:500; color:#666; font-size:12px;"
                    >
                      原発からの直線距離
                    </div>
                    <div class="range-field">
                      <input
                        v-model="searchFilters.communityDistanceFrom"
                        name="communityDistanceFrom"
                        type="text"
                        maxlength="6"
                        autocomplete="off"
                        @input="validateRange"
                      />
                      <span>〜</span>
                      <input
                        v-model="searchFilters.communityDistanceTo"
                        type="text"
                        name="communityDistanceTo"
                        maxlength="6"
                        autocomplete="off"
                        @input="validateRange"
                      />
                      <span>km</span>
                    </div>
                  </div>
                </div>
                </div>
                <div style="position:absolute; bottom:8px; right:8px;">
                  <v-btn class="btn-primary" @click="clickGetList">
                    一覧表示
                  </v-btn>
                </div>
              </div>
            </div>
          </div>
        </div>
      </details>

      <!-- 一覧部 -->
      <div class="content-card">
        <div
          style="display:flex; justify-content:space-between; align-items:center; margin-bottom:16px; flex-wrap:wrap;"
        >
          <div
            style="display:flex; align-items:center; gap:16px; flex-wrap:wrap;"
        >
            <h2 class="card-title" style="margin:0;">
              避難対象の医療機関等一覧
            </h2>
            <select
              v-model="cityFilter"
              style="padding:8px 12px; border:1px solid #c7c7c7; border-radius:4px; font-size:14px; min-width:150px;"
            >
              <option value="">避難元の市町を絞込</option>
              <option v-for="city in uniqueCities" :key="city" :value="city">{{
                city
              }}</option>
            </select>
            <div class="vtf-field">
              <input
                v-model="facilityNameFilter"
                type="text"
                style="padding:8px 12px; font-size:14px; min-width:200px;"
                placeholder=" "
                maxlength="60"
              />
              <label>避難元の施設名（カナ）で絞込</label>
            </div>
            <div style="font-size:14px;">
              ※避難元において他の医療機関等施設で調整が困難な場合、「調整困難」にチェックし<br />「確定」ボタンを押下してください。
            </div>
          </div>
          <v-card-actions>
            <v-btn class="btn-primary" @click="exportExcel">
              Excel出力
            </v-btn>
            <v-btn class="btn-primary" @click="confirm">
              確定
            </v-btn>
          </v-card-actions>
        </div>
        <div class :style="{ height: tableHeight + 'px', overflow: 'auto' }">
          <table class="table adjustment-table dsv-table">
            <thead>
              <tr>
                <th rowspan="3">調整<br />困難</th>
                <th colspan="9">避難元医療機関等</th>
                <th colspan="9">避難先医療機関等</th>
              </tr>
              <tr>
                <th
                  rowspan="2"
                  style="cursor:pointer;"
                  @click="sortBy('municip_name')"
                >
                  市町名
                  <span v-if="sortKey === 'municip_name'">
                    <i
                      :class="[
                        'fas',
                        sortOrder === 'asc' ? 'fa-sort-up' : 'fa-sort-down'
                      ]"
                    ></i>
                  </span>
                  <span v-else>
                    <i class="fas fa-sort"></i>
                  </span>
                </th>
                <th
                  rowspan="2"
                  style="cursor:pointer;"
                  @click="sortBy('area_name')"
                >
                  地区名
                  <span v-if="sortKey === 'area_name'">
                    <i
                      :class="[
                        'fas',
                        sortOrder === 'asc' ? 'fa-sort-up' : 'fa-sort-down'
                      ]"
                    ></i>
                  </span>
                  <span v-else>
                    <i class="fas fa-sort"></i>
                  </span>
                </th>
                <th
                  rowspan="2"
                  style="cursor:pointer;"
                  @click="sortBy('fa_details_type_val')"
                >
                  施設詳細種別
                  <span v-if="sortKey === 'fa_details_type_val'">
                    <i
                      :class="[
                        'fas',
                        sortOrder === 'asc' ? 'fa-sort-up' : 'fa-sort-down'
                      ]"
                    ></i>
                  </span>
                  <span v-else>
                    <i class="fas fa-sort"></i>
                  </span>
                </th>
                <th
                  rowspan="2"
                  style="cursor:pointer;"
                  @click="sortBy('fa_name')"
                >
                  施設名
                  <span v-if="sortKey === 'fa_name'">
                    <i
                      :class="[
                        'fas',
                        sortOrder === 'asc' ? 'fa-sort-up' : 'fa-sort-down'
                      ]"
                    ></i>
                  </span>
                  <span v-else>
                    <i class="fas fa-sort"></i>
                  </span>
                </th>
                <th
                  rowspan="2"
                  style="cursor:pointer;"
                  @click="sortBy('fa_name_kana')"
                >
                  施設名（カナ）
                  <span v-if="sortKey === 'fa_name_kana'">
                    <i
                      :class="[
                        'fas',
                        sortOrder === 'asc' ? 'fa-sort-up' : 'fa-sort-down'
                      ]"
                    ></i>
                  </span>
                  <span v-else>
                    <i class="fas fa-sort"></i>
                  </span>
                </th>
                <th rowspan="2">住所</th>
                <th rowspan="2">病床数</th>
                <th colspan="2">原発からの</th>
                <th
                  rowspan="2"
                  style="cursor:pointer;"
                  @click="sortBy('eva_municip_name')"
                >
                  市町名
                  <span v-if="sortKey === 'eva_municip_name'">
                    <i
                      :class="[
                        'fas',
                        sortOrder === 'asc' ? 'fa-sort-up' : 'fa-sort-down'
                      ]"
                    ></i>
                  </span>
                  <span v-else>
                    <i class="fas fa-sort"></i>
                  </span>
                </th>
                <th
                  rowspan="2"
                  style="cursor:pointer;"
                  @click="sortBy('eva_fa_details_type_val')"
                >
                  施設詳細種別
                  <span v-if="sortKey === 'eva_fa_details_type_val'">
                    <i
                      :class="[
                        'fas',
                        sortOrder === 'asc' ? 'fa-sort-up' : 'fa-sort-down'
                      ]"
                    ></i>
                  </span>
                  <span v-else>
                    <i class="fas fa-sort"></i>
                  </span>
                </th>
                <th
                  rowspan="2"
                  style="cursor:pointer;"
                  @click="sortBy('eva_fa_name')"
                >
                  施設名
                  <span v-if="sortKey === 'eva_fa_name'">
                    <i
                      :class="[
                        'fas',
                        sortOrder === 'asc' ? 'fa-sort-up' : 'fa-sort-down'
                      ]"
                    ></i>
                  </span>
                  <span v-else>
                    <i class="fas fa-sort"></i>
                  </span>
                </th>
                <th
                  rowspan="2"
                  style="cursor:pointer;"
                  @click="sortBy('eva_fa_name_kana')"
                >
                  施設名（カナ）
                  <span v-if="sortKey === 'eva_fa_name_kana'">
                    <i
                      :class="[
                        'fas',
                        sortOrder === 'asc' ? 'fa-sort-up' : 'fa-sort-down'
                      ]"
                    ></i>
                  </span>
                  <span v-else>
                    <i class="fas fa-sort"></i>
                  </span>
                </th>
                <th rowspan="2">住所</th>
                <th rowspan="2">病床数</th>
                <th rowspan="2">受入<br />可能者数</th>
                <th colspan="2">原発からの</th>
              </tr>
              <tr>
                <th>方角</th>
                <th>直線距離</th>
                <th>方角</th>
                <th>直線距離</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="facility in sortedFacilities" :key="facility.key">
                <td>
                  <input type="checkbox" v-model="facility.unacceptable_flg" />
                </td>
                <td>{{ facility.municip_name }}</td>
                <td>{{ facility.area_name }}</td>
                <td>{{ facility.fa_details_type_val }}</td>
                <td>{{ facility.fa_name }}</td>
                <td>{{ facility.fa_name_kana }}</td>
                <td>{{ facility.fa_address }}</td>
                <td>{{ facility.max_capacity }}</td>
                <td>{{ facility.direction_from_npp }}</td>
                <td>{{ facility.distance_from_npp }} km</td>

                <td>{{ facility.eva_municip_name }}</td>
                <td>{{ facility.eva_fa_details_type_val }}</td>
                <td>{{ facility.eva_fa_name }}</td>
                <td>{{ facility.eva_fa_name_kana }}</td>
                <td>{{ facility.eva_fa_address }}</td>
                <td>{{ facility.eva_max_capacity }}</td>
                <td>{{ facility.eva_acceptable_capacity }}</td>
                <td>{{ facility.eva_direction_from_npp }}</td>
                <td>
                  <div v-if="facility.eva_distance_from_npp">
                    {{ facility.eva_distance_from_npp }} km
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </section>
    <ConfirmDialog ref="confirmDialog" />
    <ErrorDialog ref="errorDialog" />
    <v-overlay :value="overlay" opacity="1" color="white">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
  </div>
  <div v-else>
    <div v-if="disasterNo">
    <error-message :error-object="errorObject" />
  </div>
  </div>
</template>

<script>
import ExcelJS from "exceljs";
import messageUtils from "@/mixins/message-utils";
import { ErrorTodoMessages } from "@/js/constants/error-messages";
import { RepositoryFactory } from "@/js/repository/RepositoryFactory";
import { ScreenId } from "@/js/value-objects/screen-id";

const adjustmentSystemRepository = RepositoryFactory.get("adjustmentSystem");
const radiationMonitoringRepository = RepositoryFactory.get(
  "radiationMonitoring"
);
const evacuationTargetVulRepository = RepositoryFactory.get(
  "evacuationTargetVul"
);

const LIST_MAX_HEIGHT = 595;
const LIST_MIN_HEIGHT = 200;

export default {
  name: "EvacuationTargetVul",
  mixins: [messageUtils],
  components: {
    ErrorMessage: () => import("@/components/ErrorMessage"),
    SuccessMessage: () => import("@/components/SuccessMessage"),
    ErrorDialog: () => import("@/components/adjustment/AdErrorDialog"),
    ConfirmDialog: () => import("@/components/adjustment/AdConfirmDialog"),
    SelectDirection: () => import("@/components/adjustment/SelectDirection"),
    SelectMonitoringPost: () =>
      import("@/components/adjustment/SelectMonitoringPost")
  },
  props: {
    disasterNo: {
      type: String,
      default: ""
    }
  },
  data: () => ({
    overlay: false,
    isOpen: true,
    isAuth: false,
    isInitError: false,
    cityFilter: "",
    facilityNameFilter: "",
    searchFilters: {
      municipName: "",
      areaName: "",
      communityDistanceFrom: null,
      communityDistanceTo: null
    },
    sortKey: "",
    sortOrder: "asc",
    facilityList: []
  }),

  computed: {
    // 検索条件表示非表示の時の一覧の高さ調整
    tableHeight() {
      return this.isOpen ? LIST_MIN_HEIGHT : LIST_MAX_HEIGHT;
    },
    // 一覧の市町名、施設名フリガナの絞込み
    filteredFacilities() {
      return this.facilityList.filter(facility => {
        if (this.cityFilter && facility.municip_name !== this.cityFilter)
          return false;
        if (
          this.facilityNameFilter &&
          !facility.fa_name_kana.includes(this.facilityNameFilter)
        )
          return false;
        return true;
      });
    },
    // 一覧のソート
    sortedFacilities() {
      const filtered = this.filteredFacilities;
      if (!this.sortKey) return filtered;

      return [...filtered].sort((a, b) => {
        const aVal = a[this.sortKey];
        const bVal = b[this.sortKey];
        const modifier = this.sortOrder === "asc" ? 1 : -1;

        if (aVal < bVal) return -1 * modifier;
        if (aVal > bVal) return 1 * modifier;
        return 0;
      });
    },
    // 一覧の市町名絞込みリスト作成
    uniqueCities() {
      const cities = [
        ...new Set(this.facilityList.map(facility => facility.municip_name))
      ];
      return cities.filter(city => city).sort();
    }
  },

  async mounted() {
    await this.initialize();
    if (this.disasterNo === "" || this.isInitError) return;
    if (this.isAuth) await this.fetchMonitoringPosts();
  },

  methods: {
    // 初期処理
    async initialize() {
      this.clearMessage();
      this.overlay = true;
      try {
        this.clearSort();
        /* 権限取得 */
        const payload = {
          disasterNo: this.disasterNo
        };
        await adjustmentSystemRepository
          .list(ScreenId.SF4101, payload)
          .then(response => {
            this.isInitError = false;
            this.isAuth = response.data.isAuth;
          })
          .catch(e => {
            this.isInitError = true;
            this.handleUpdateError(e);
          });
      } catch (error) {
        this.isInitError = true;
        this.handleUpdateError(error);
        console.error("ユーザーの権限取得に失敗しました:", error);
      } finally {
        this.overlay = false;
      }
    },
    // 検索条件開閉
    toggleOpen() {
      this.isOpen = !this.isOpen;
    },
    // モニタリングポスト一覧取得
    async fetchMonitoringPosts() {
      this.overlay = true;
      try {
        /* 放射線モニタリング一覧取得 */
        const payload = {};
        await radiationMonitoringRepository
          .list(ScreenId.SF4101, payload)
          .then(response => {
            this.$refs.selectMonitoringPost.setMonitoringPosts(
              response.data.results
            );
          })
          .catch(e => {
            this.handleUpdateError(e);
          });
      } catch (error) {
        this.handleUpdateError(error);
        console.error("モニタリングポスト一覧の取得に失敗しました:", error);
      } finally {
        this.overlay = false;
      }
    },
    validateRange(e) {
      const key = e.target.name;
      let txtValue = e.target.value.replace(/[^0-9.]/g, "");
      let value = parseFloat(txtValue);
      if (isNaN(value)) {
        this.searchFilters[key] = txtValue;
      }
      if (value < 0.0) txtValue = 0.0;
      if (value > 9999.9) txtValue = 9999.9;
      this.searchFilters[key] = txtValue;
    },
    // 一覧のソート処理
    sortBy(key) {
      if (this.sortKey === key) {
        this.sortOrder = this.sortOrder === "asc" ? "desc" : "asc";
      } else {
        this.sortKey = key;
        this.sortOrder = "asc";
      }
    },
    clearSort() {
      this.sortKey = "";
      this.sortOrder = "asc";
    },
    async clickGetList() {
      this.clearMessage();
      await this.getList();
      this.cityFilter = "";
      this.facilityNameFilter = "";
    },
    // 一覧の取得
    async getList() {
      this.overlay = true;
      try {
        /* 避難先の医療機関等一覧取得 */
        this.clearSort();
        const directions = await this.$refs.selectDirection.getSelectDirection();
        const monitoringPosts = await this.$refs.selectMonitoringPost.getMonitoringPosts();

        const payload = {
          disasterNo: this.disasterNo,
          directionList: directions,
          stationList: monitoringPosts.map(post => post.station_id),
          municipName: this.searchFilters.municipName,
          areaName: this.searchFilters.areaName,
          distanceFrom: this.searchFilters.communityDistanceFrom,
          distanceTo: this.searchFilters.communityDistanceTo
        };

        await evacuationTargetVulRepository
          .list(ScreenId.SF4101, payload)
          .then(response => {
            this.facilityList = response.data.results;
            this.isOpen = false;
          })
          .catch(e => {
            this.handleUpdateError(e);
          });
      } catch (error) {
        this.handleUpdateError(error);
        console.error("避難先の医療機関等一覧の取得に失敗しました:", error);
      } finally {
        this.overlay = false;
      }
    },
    // EXCEL出力
    async exportExcel() {
      this.overlay = true;
      try {
        if (!this.facilityList.length > 0) {
          await this.$refs.errorDialog.open(ErrorTodoMessages.ERWE48001);
          return;
        }

        /* タイムスタンプ算出 */
        let now = new Date();
        let year = now.getFullYear();
        let month = ("00" + (now.getMonth() + 1)).slice(-2);
        let day = ("00" + now.getDate()).slice(-2);
        let hour = ("00" + now.getHours()).slice(-2);
        let minute = ("00" + now.getMinutes()).slice(-2);
        let timeStamp = year + month + day + hour + minute;
        let timeStamp2 =
          year + "/" + month + "/" + day + " " + hour + ":" + minute;

        // 固定のExcelファイルを取得（publicフォルダに配置されていると仮定）
        const response = await fetch(
          "/template/evacuation_target_vul_list.xlsx"
        );
        const arrayBuffer = await response.arrayBuffer();

        const workbook = new ExcelJS.Workbook();
        await workbook.xlsx.load(arrayBuffer);

        const worksheet = workbook.getWorksheet(1); // 最初のシートを取得

        // 罫線スタイルを定義
        const borderStyle = {
          style: "thin",
          color: { argb: "FF000000" }
        };

        //5行目まではヘッダー
        var rowNo = 5;
        this.facilityList.forEach(data => {
          if (this.cityFilter && data.municip_name !== this.cityFilter) return;
          if (
            this.facilityNameFilter &&
            !data.fa_name_kana.includes(this.facilityNameFilter)
          )
            return;

          rowNo++;
          const row = worksheet.insertRow(rowNo, [
            rowNo - 5,
            data.unacceptable_flg_db ? "○" : "",

            data.municip_name,
            data.area_name,
            data.fa_details_type_val,
            data.fa_name,
            data.fa_name_kana,
            data.fa_address,
            Number(data.max_capacity),
            data.direction_from_npp,
            data.distance_from_npp + " km",

            data.eva_municip_name,
            data.eva_fa_details_type_val,
            data.eva_fa_name,
            data.eva_fa_name_kana,
            data.eva_fa_address,
            Number(data.eva_max_capacity),
            Number(data.eva_acceptable_capacity),
            data.eva_direction_from_npp,
            data.eva_distance_from_npp + " km"
          ]);

          // 各セルに罫線を設定
          row.eachCell(cell => {
            cell.border = {
              top: borderStyle,
              left: borderStyle,
              bottom: borderStyle,
              right: borderStyle
            };
          });
        });

        const cell = worksheet.getCell("T2");
        cell.value = "出力日時：" + timeStamp2;

        // 編集済みファイルをダウンロード
        const editedBuffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([editedBuffer], {
          type:
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        });

        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = "避難対象の医療機関等一覧_" + timeStamp + ".xlsx";
        link.click();
      } catch (error) {
        console.error("EXCEL出力に失敗しました:", error);
      } finally {
        this.overlay = false;
      }
    },
    // 確定処理
    async confirm() {
      try {
        if (!this.facilityList.length > 0) {
          await this.$refs.errorDialog.open(ErrorTodoMessages.ERWE48001);
          return;
        }

        // 更新確認ダイアログを表示
        const message = `避難対象の医療機関等の情報を確定します。よろしいですか？`;
        const result = await this.$refs.confirmDialog.open(message, "確定");
        if (!result) {
          return false;
        }

        this.clearMessage();
        this.overlay = true;

        const facilityStatusList = [];
        this.facilityList.forEach(facility => {
          if (this.cityFilter && facility.municip_name !== this.cityFilter)
            return;
          if (
            this.facilityNameFilter &&
            !facility.fa_name_kana.includes(this.facilityNameFilter)
          )
            return;

          facilityStatusList.push({
            vulFaId: facility.fa_id,
            evaFaId: facility.eva_fa_id,
            unavailableFlg: facility.unacceptable_flg || false
          });
        });

        if (!facilityStatusList.length > 0) {
          await this.$refs.errorDialog.open(ErrorTodoMessages.ERWE48001);
          return;
        }

        const payload = {
          disasterNo: this.disasterNo,
          list: facilityStatusList
        };

        let isSuccess = false;
        await evacuationTargetVulRepository
          .update(ScreenId.SF4101, payload)
          .then(response => {
            isSuccess = true;
            this.handleSuccess(response);
          })
          .catch(error => {
            this.handleUpdateError(error);
          });

        if (isSuccess) {
          // 登録後の最新データを取得する
          await this.getList();
        }
      } catch (error) {
        console.error("避難先の医療機関等一覧の確定に失敗しました:", error);
      } finally {
        this.overlay = false;
      }
    }
  }
};
</script>
